import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  Node,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useConversation } from '../../../contexts/ConversationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useChatDagRuntime } from '../../../contexts/ChatDagRuntimeContext';
import { useChatDagDimensions } from '../../../contexts/ChatDagDimensionsContext';
import { usePersonaChatUI } from '../../../contexts/PersonaChatUIContext';
import { useToast } from '../../../contexts/ToastContext';
import { chatDagNodeTypes } from './nodes';
import { messagesToDag } from './utils/chatDagDataProcessor';
import { layoutChatDag } from './utils/chatDagLayout';
import { useAncestorMap } from './hooks/useAncestorMap';
import { useTweenedNodePositions } from './hooks/useTweenedNodePositions';

// Camera-fit constants — single source of truth for all fitView() calls
// in this file. Three classes of fit:
//   USER  — user-initiated nav (panel row click, minimap AABB click,
//           "fit graph" button). Smooth + a touch of breathing room.
//   LIVE  — autofit during an in-flight turn. Same duration as USER so
//           rapid switching feels coherent; tighter padding so the live
//           work fills the frame; softened maxZoom (was 1.3 → 1.2) to
//           stop the over-punch-in jitter when a small stack starts.
//   FULL  — "fit the whole DAG" (turn end, fit-graph button). Slightly
//           tighter padding than USER so the wide frame doesn't waste
//           edge real estate.
// Inconsistent durations/padding across handlers (was 300/350/600 ms with
// 0.15/0.25/0.3 padding) was the visible-jitter source.
const USER_FIT_DURATION = 600;
const USER_FIT_PADDING = 0.22;
const USER_FIT_MAX_ZOOM = 1.1;
const LIVE_FIT_DURATION = 600;
const LIVE_FIT_PADDING = 0.3;
const LIVE_FIT_MAX_ZOOM = 1.2;
const FULL_FIT_PADDING = 0.18;

// Chat DAG — top-down temporal visualization of the session's tool
// orchestration.
//
// Each turn lays out as a small subgraph: user marker → LLM rounds
// (fanning out tool calls each round, with consult_peers fanning
// further into peers) → assistant marker. Turns chain end-to-start.
// All data comes from ConversationContext messages — no live
// WS stream needed, the Wave 6 response shape carries enough.
//
// Nodes are first-class React components (see nodes/*); anything you
// want to "hang" on the DAG (cost chips, highlight overlays, click
// handlers) lives there. The canvas itself is intentionally thin.

// Controls panel rendered inside <ReactFlow> via a <Panel> pinned
// top-right with a margin-top offset so it sits UNDER the MiniMap
// (which is top-right, 100px tall).
// Globals section:
//   ⤢ fit — ReactFlow fitView
//   [ ] collapse all phase stacks — global: every turn renders as a
//       single collapsed_phase_stack card
//   [ ] collapse all peer fans — global: every consult_peers fan
//       renders as a FanCollapsedChip
//   [x] show phase stack boxes — toggle the per-turn phase_stack_group
//       bounding-box backdrops
// Stacks section (one line per turn):
//   [x] Stack N: <auto-derived name>   🎯
//   Checkbox toggles per-turn collapse; 🎯 button fits the DAG view
//   on that stack and scrolls the chat pane to the user bubble.
const ControlsPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { state } = useConversation();
  const runtime = useChatDagRuntime();
  const {
    setAllFansCollapsed,
    setShowPhaseStacks,
    togglePhaseStackCollapse,
    setAllPhaseStacksCollapsed,
    focusBubble,
  } = usePersonaChatUI();
  const { fitView } = useReactFlow();

  const [moreOpen, setMoreOpen] = useState(false);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (moreBtnRef.current && moreBtnRef.current.contains(t)) return;
      if (popoverRef.current && popoverRef.current.contains(t)) return;
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [moreOpen]);

  // Derive the phase-stack list from messages: one entry per turn.
  // We keep the FULL prompt text now — truncation is CSS-driven via
  // line-clamp + linear-gradient mask so the panel can show two lines
  // of context per stack instead of a single 48-char slice.
  const stacks = useMemo(() => {
    const out: Array<{
      turnKey: string;
      turnIndex: number;
      fullText: string;
    }> = [];
    let lastUser: { id: string; content: string } | null = null;
    let idx = 0;
    for (const m of state.messages) {
      if (m.role === 'user') {
        lastUser = { id: m.id, content: m.content ?? '' };
      } else if (m.role === 'assistant' && lastUser) {
        const raw = lastUser.content.replace(/\s+/g, ' ').trim();
        out.push({
          turnKey: lastUser.id,
          turnIndex: idx,
          fullText: raw || '(empty prompt)',
        });
        idx += 1;
        lastUser = null;
      }
    }
    return out;
  }, [state.messages]);

  const handleTarget = React.useCallback(
    (turnKey: string) => {
      // Fit the DAG on this stack's node. Phase-stack-group when the
      // box is visible; otherwise the collapsed card id.
      const groupId = runtime.collapsedPhaseStackIds.has(turnKey) ||
        runtime.allPhaseStacksCollapsed
        ? `stack-${turnKey}`
        : `group-stack-${turnKey}`;
      try {
        fitView({
          nodes: [{ id: groupId }],
          padding: USER_FIT_PADDING,
          duration: USER_FIT_DURATION,
          maxZoom: USER_FIT_MAX_ZOOM,
        });
      } catch {
        // Node not rendered — no-op.
      }
      // Also scroll the chat pane to the matching user bubble.
      focusBubble(turnKey);
    },
    [
      fitView,
      focusBubble,
      runtime.collapsedPhaseStackIds,
      runtime.allPhaseStacksCollapsed,
    ],
  );

  const bg = darkMode
    ? 'bg-gray-900/95 border-gray-700 text-gray-200'
    : 'bg-white/95 border-gray-300 text-gray-700';

  return (
    <div
      className={`relative flex flex-col gap-1 px-2 py-1.5 rounded-md border shadow-md ${bg}`}
      style={{ width: 240 }}
    >
      {/* Header: count chip (mono chrome) + ⋯ overflow */}
      <div className="flex items-center justify-between">
        <span
          className={`text-[9px] font-mono uppercase tracking-widest ${
            darkMode ? 'text-emerald-400/70' : 'text-emerald-700/70'
          }`}
          title={`${stacks.length} phase ${stacks.length === 1 ? 'stack' : 'stacks'}`}
        >
          {stacks.length} {stacks.length === 1 ? 'stack' : 'stacks'}
        </span>
        <button
          type="button"
          ref={moreBtnRef}
          onClick={() => setMoreOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          title="More controls"
          className={`text-[12px] leading-none px-1.5 py-0.5 rounded transition-colors ${
            darkMode
              ? 'text-gray-300 hover:bg-white/10'
              : 'text-gray-700 hover:bg-black/5'
          }`}
        >
          ⋯
        </button>
      </div>

      {/* Globals popover, anchored under the ⋯ */}
      {moreOpen && (
        <div
          ref={popoverRef}
          role="menu"
          className={`absolute right-1 top-7 z-30 flex flex-col gap-1 px-2 py-2 rounded-md border shadow-lg text-[10px] font-mono ${bg}`}
          style={{ width: 220 }}
        >
          <button
            type="button"
            onClick={() => {
              fitView({
                padding: FULL_FIT_PADDING,
                duration: USER_FIT_DURATION,
                maxZoom: USER_FIT_MAX_ZOOM,
              });
              setMoreOpen(false);
            }}
            className={`text-left px-1 py-0.5 rounded uppercase tracking-wider text-[9px] ${
              darkMode
                ? 'text-emerald-300 hover:bg-emerald-900/30'
                : 'text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            ⤢ fit graph
          </button>
          <div className={`h-px ${darkMode ? 'bg-gray-700/70' : 'bg-gray-200'}`} />
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={runtime.allPhaseStacksCollapsed}
              onChange={(e) => setAllPhaseStacksCollapsed(e.target.checked)}
              className="h-3 w-3 accent-emerald-500 cursor-pointer"
            />
            <span>collapse all phase stacks</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={runtime.allFansCollapsed}
              onChange={(e) => setAllFansCollapsed(e.target.checked)}
              className="h-3 w-3 accent-emerald-500 cursor-pointer"
            />
            <span>collapse all peer fans</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={runtime.showPhaseStacks}
              onChange={(e) => setShowPhaseStacks(e.target.checked)}
              className="h-3 w-3 accent-emerald-500 cursor-pointer"
            />
            <span>show phase stack boxes</span>
          </label>
        </div>
      )}

      {/* Stack list — pinned spine + 2-line sans-serif preview + hover chevron */}
      {stacks.length > 0 && (
        <div
          className={`flex flex-col gap-0.5 pt-1 border-t ${
            darkMode ? 'border-gray-700/70' : 'border-gray-200'
          }`}
        >
          {stacks.map((s) => {
            const isCollapsed =
              runtime.allPhaseStacksCollapsed ||
              runtime.collapsedPhaseStackIds.has(s.turnKey);
            const isViewport = runtime.viewportTurnId === s.turnKey;
            const isInFlight = runtime.inFlightTurnId === s.turnKey;
            const isError = runtime.errorTurnId === s.turnKey;
            // Color-match the row state to PhaseStackGroupNode's AABB.
            // PhaseStackGroupNode (nodes/PhaseStackGroupNode.tsx:36-50):
            //   error    → rose border + rose fill
            //   inFlight → bright emerald border + glow
            //   viewport → dim emerald border
            //   default  → gray border
            // Row mirrors via its left gutter rail + pin color so the
            // panel-side selection visually pairs with the canvas-side
            // AABB without any leader-line overlay.
            let railClass = '';
            let pinClass = '';
            let rowBgClass = '';
            if (isError) {
              railClass = darkMode ? 'bg-rose-400' : 'bg-rose-500';
              pinClass = darkMode
                ? 'bg-rose-400 ring-rose-300/40'
                : 'bg-rose-500 ring-rose-600/40';
              rowBgClass = darkMode ? 'bg-rose-950/20' : 'bg-rose-50/60';
            } else if (isInFlight) {
              railClass = darkMode ? 'bg-emerald-300' : 'bg-emerald-500';
              pinClass = darkMode
                ? 'bg-emerald-300 ring-emerald-200/50'
                : 'bg-emerald-500 ring-emerald-600/50';
              rowBgClass = darkMode ? 'bg-emerald-900/25' : 'bg-emerald-50/80';
            } else if (isViewport) {
              railClass = darkMode ? 'bg-emerald-500' : 'bg-emerald-500';
              pinClass = darkMode
                ? 'bg-emerald-400 ring-emerald-300/40'
                : 'bg-emerald-500 ring-emerald-600/40';
              rowBgClass = darkMode ? 'bg-emerald-950/20' : 'bg-emerald-50/40';
            } else {
              pinClass = darkMode
                ? 'bg-slate-500/70 ring-slate-400/30'
                : 'bg-slate-400/80 ring-slate-500/30';
              rowBgClass = 'hover:bg-white/[0.02] dark:hover:bg-white/[0.03]';
            }
            return (
              <div
                key={s.turnKey}
                data-turn-key={s.turnKey}
                className={`group relative flex items-stretch gap-2 pr-1 py-1 rounded transition-colors ${rowBgClass}`}
              >
                {/* Active gutter rail — mirrors AABB border color */}
                {(isViewport || isInFlight || isError) && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-0 top-1 bottom-1 w-[1.5px] rounded ${railClass}`}
                  />
                )}
                {/* Pin gutter — color mirrors AABB state */}
                <span
                  aria-hidden="true"
                  className="relative flex items-start justify-center w-3 pt-1.5 flex-shrink-0"
                >
                  <span
                    className={`block w-1.5 h-1.5 rounded-sm ring-1 ${pinClass}`}
                  />
                  {isInFlight && (
                    <span
                      aria-hidden="true"
                      className={`absolute inset-0 m-auto w-3 h-3 rounded-full animate-ping ${
                        darkMode ? 'bg-emerald-400/40' : 'bg-emerald-500/30'
                      }`}
                    />
                  )}
                </span>
                {/* Number + 2-line preview (sans-serif content; mono number) */}
                <button
                  type="button"
                  onClick={() => handleTarget(s.turnKey)}
                  title={s.fullText}
                  className={`flex-1 min-w-0 text-left ${
                    darkMode ? 'hover:text-emerald-300' : 'hover:text-emerald-700'
                  }`}
                >
                  <span className="block text-[9px] font-mono opacity-50 tabular-nums leading-none mb-0.5">
                    {s.turnIndex + 1}
                  </span>
                  <span
                    className="block font-sans text-[12px] font-medium leading-snug overflow-hidden"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maskImage:
                        'linear-gradient(to bottom, black 70%, transparent 100%)',
                      WebkitMaskImage:
                        'linear-gradient(to bottom, black 70%, transparent 100%)',
                    }}
                  >
                    {s.fullText}
                  </span>
                </button>
                {/* Hover chevron — collapse toggle */}
                <button
                  type="button"
                  onClick={() => togglePhaseStackCollapse(s.turnKey)}
                  disabled={runtime.allPhaseStacksCollapsed}
                  title={
                    runtime.allPhaseStacksCollapsed
                      ? 'Disabled while "collapse all phase stacks" is on'
                      : isCollapsed
                        ? 'Expand this stack'
                        : 'Collapse this stack'
                  }
                  aria-label={isCollapsed ? 'Expand stack' : 'Collapse stack'}
                  className={`self-start mt-1 text-[12px] leading-none w-4 h-4 flex items-center justify-center rounded transition-opacity ${
                    runtime.allPhaseStacksCollapsed
                      ? 'opacity-20 cursor-not-allowed'
                      : 'opacity-0 group-hover:opacity-60 hover:!opacity-100'
                  } ${
                    darkMode
                      ? 'text-gray-300 hover:bg-white/10'
                      : 'text-gray-700 hover:bg-black/5'
                  }`}
                >
                  {isCollapsed ? '▸' : '▾'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface ChatDagInnerProps {
  primaryPersona?: any;
}

const ChatDagInner: React.FC<ChatDagInnerProps> = ({ primaryPersona }) => {
  const { darkMode } = useTheme();
  const { state } = useConversation();
  const runtime = useChatDagRuntime();
  const { dimensions } = useChatDagDimensions();
  const { fitView, getViewport, setViewport } = useReactFlow();

  const { nodes, edges, turnBoundingBoxes } = useMemo(() => {
    const raw = messagesToDag(state.messages, {
      inFlightTurnId: runtime.inFlightTurnId,
      viewportTurnId: runtime.viewportTurnId,
      collapsedFanIds: runtime.collapsedFanIds,
      livePhaseEvents: runtime.livePhaseEvents,
      wsLastEventMs: runtime.wsLastEventMs,
      allFansCollapsed: runtime.allFansCollapsed,
      collapsedPhaseStackIds: runtime.collapsedPhaseStackIds,
      allPhaseStacksCollapsed: runtime.allPhaseStacksCollapsed,
      primaryPersona,
    });
    const result = layoutChatDag(
      raw.nodes,
      raw.edges,
      dimensions,
      runtime.focalTurnId,
      runtime.inFlightStartedAtMs,
    );
    let laidOut = result.nodes;
    // Controls panel: user can hide the phase_stack_group backdrops
    // without affecting the rest of the DAG. Filtered out post-layout
    // so positioning of everything else is unchanged.
    if (!runtime.showPhaseStacks) {
      laidOut = laidOut.filter((n) => n.type !== 'phase_stack_group');
    }
    return {
      nodes: laidOut,
      edges: result.edges,
      turnBoundingBoxes: result.turnBoundingBoxes,
    };
  }, [
    state.messages,
    runtime.inFlightTurnId,
    runtime.viewportTurnId,
    runtime.collapsedFanIds,
    runtime.livePhaseEvents,
    runtime.wsLastEventMs,
    runtime.allFansCollapsed,
    runtime.showPhaseStacks,
    runtime.collapsedPhaseStackIds,
    runtime.allPhaseStacksCollapsed,
    runtime.focalTurnId,
    runtime.inFlightStartedAtMs,
    dimensions,
    primaryPersona,
  ]);

  // v0.4 Task 9 → Task 13 polish → 2026-04-24 first-turn fix: autofit
  // tracks the in-flight turn only. Viewport-driven focal changes drive
  // rich dispatch + dim resolution (Tasks 6/7/8) but do NOT camera-follow
  // — the user's own scroll is the camera command there, and forcing
  // fitView on every scroll turn-change handcuffs the view to one turn
  // at a time (the original "camera chase on scroll" regression,
  // `ccd72547`).
  //
  // Triggers (both collapse into this one effect):
  // - inFlightTurnId transition (start / end)       → snap in, or widen out
  // - in-flight turn's bounding box grows            → track the unfolding stack
  //
  // The bbox trigger fixes the first-turn-of-a-new-chat case: on initial
  // mount the DAG has maybe one node (user marker) when inFlight starts;
  // fitView locks to that single node and the camera froze top-left as
  // rounds/tools/peers streamed in. Bucketing bbox to 4px suppresses
  // jitter from micro-layout churn; fitView fires only on meaningful
  // growth, not every render.
  //
  // cameraBusy still fires on transitions so the tween hook switches
  // arrivals to fade-only while the fitView tween runs.
  const inFlightBBoxKey = useMemo(() => {
    const id = runtime.inFlightTurnId;
    if (!id) return null;
    const b = turnBoundingBoxes.get(id);
    if (!b) return null;
    return `${Math.round(b.w / 4)}x${Math.round(b.h / 4)}`;
  }, [runtime.inFlightTurnId, turnBoundingBoxes]);

  const [cameraBusy, setCameraBusy] = useState(false);
  useEffect(() => {
    const id = runtime.inFlightTurnId;
    setCameraBusy(true);
    const h = window.setTimeout(() => {
      try {
        if (id) {
          const liveNodes = nodes.filter(
            (n) => (n.data as any)?.turnKey === id,
          );
          if (liveNodes.length > 0) {
            fitView({
              nodes: liveNodes.map((n) => ({ id: n.id })),
              padding: LIVE_FIT_PADDING,
              duration: LIVE_FIT_DURATION,
              maxZoom: LIVE_FIT_MAX_ZOOM,
            });
          } else {
            fitView({
              padding: FULL_FIT_PADDING,
              duration: LIVE_FIT_DURATION,
              maxZoom: USER_FIT_MAX_ZOOM,
            });
          }
        } else {
          // Turn ended — widen to the full DAG.
          fitView({
            padding: FULL_FIT_PADDING,
            duration: LIVE_FIT_DURATION,
            maxZoom: USER_FIT_MAX_ZOOM,
          });
        }
      } catch {
        /* unmounted */
      }
    }, 50);
    const t = window.setTimeout(
      () => setCameraBusy(false),
      LIVE_FIT_DURATION + 80,
    );
    return () => {
      window.clearTimeout(h);
      window.clearTimeout(t);
    };
    // `nodes` is intentionally not a dep — that would refire on every
    // position update. Instead we gate on inFlightBBoxKey which changes
    // only when the in-flight turn's AABB grew past the 4px bucket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.inFlightTurnId, inFlightBBoxKey, fitView]);

  // v0.4 Task 11: scroll anchor. When the focal turn's bounding
  // box shifts between layouts (e.g. a new tool call landed in an
  // earlier turn and pushed this one down), shift the viewport so
  // the focal turn stays pinned to the same screen position. Without
  // this, the user's camera jumps every time a new structural
  // change ripples through.
  //
  // NOTE: delta signs may need flipping based on ReactFlow viewport
  // orientation — verify in browser; architect A4' licenses a fix
  // here in-scope if inverted.
  const prevBoundsRef = useRef<Map<
    string,
    { x: number; y: number; w: number; h: number }
  > | null>(null);
  useLayoutEffect(() => {
    const focal = runtime.focalTurnId;
    if (focal == null || prevBoundsRef.current == null) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    const prev = prevBoundsRef.current.get(focal);
    const curr = turnBoundingBoxes.get(focal);
    if (!prev || !curr) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    const deltaX = curr.x - prev.x;
    const deltaY = curr.y - prev.y;
    if (Math.abs(deltaY) < 2 && Math.abs(deltaX) < 2) {
      prevBoundsRef.current = new Map(turnBoundingBoxes);
      return;
    }
    try {
      const vp = getViewport();
      setViewport({
        x: vp.x - deltaX * vp.zoom,
        y: vp.y - deltaY * vp.zoom,
        zoom: vp.zoom,
      });
    } catch {
      /* unmounted */
    }
    prevBoundsRef.current = new Map(turnBoundingBoxes);
  }, [
    turnBoundingBoxes,
    runtime.focalTurnId,
    getViewport,
    setViewport,
  ]);

  // v0.4 HANDBRAKE: the tween hook has a useMemo cache bug — its
  // projection depends on performance.now() but the deps only track
  // inputs, so post-first-render RAF ticks return cached values
  // and positions freeze at tween-start (ancestor position, often
  // x=0). Every node stacked at origin. Bypassing — use raw layout
  // positions. Lift-in arrival + smoothstep cancel-restart stays on
  // disk for a proper v0.4.1 fix.
  void useAncestorMap;
  void useTweenedNodePositions;
  void cameraBusy;
  const tweenedNodes = nodes;

  const isEmpty = nodes.length === 0;

  // Debug snapshot button — emits a schema-versioned JSON blob with
  // nodes, edges, runtime, flag state, and a trimmed recent-messages
  // tail so we can paste it back into Claude when iterating on DAG
  // behavior. Added after the "DAG sat still, then snapped to full"
  // live-update bug investigation.
  const { showToast } = useToast();
  const handleCopyDebugSnapshot = React.useCallback(async () => {
    const recent = state.messages.slice(-6).map((m) => ({
      id: m.id,
      role: m.role,
      timestamp: m.timestamp,
      snippet: (m.content ?? '').slice(0, 120),
      toolCallCount: Array.isArray((m.metadata as any)?.tool_calls)
        ? (m.metadata as any).tool_calls.length
        : 0,
    }));
    const payload = {
      schema: 'chatdag-debug-snapshot-v1',
      capturedAt: new Date().toISOString(),
      counts: {
        nodes: nodes.length,
        edges: edges.length,
        messages: state.messages.length,
        livePhaseEvents: runtime.livePhaseEvents.length,
      },
      runtime: {
        inFlightTurnId: runtime.inFlightTurnId,
        viewportTurnId: runtime.viewportTurnId,
        errorTurnId: runtime.errorTurnId,
        collapsedFanIds: Array.from(runtime.collapsedFanIds),
        allFansCollapsed: runtime.allFansCollapsed,
        showPhaseStacks: runtime.showPhaseStacks,
        collapsedPhaseStackIds: Array.from(runtime.collapsedPhaseStackIds),
        allPhaseStacksCollapsed: runtime.allPhaseStacksCollapsed,
        wsLastEventMs: runtime.wsLastEventMs,
        wsLastEventAgoMs:
          runtime.wsLastEventMs != null
            ? Date.now() - runtime.wsLastEventMs
            : null,
      },
      recentMessages: recent,
      nodes,
      edges,
      livePhaseEvents: runtime.livePhaseEvents,
    };
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      showToast(
        `DAG snapshot copied (${nodes.length}n/${edges.length}e, ${runtime.livePhaseEvents.length} live events).`,
        'success',
      );
    } catch {
      // Some browsers (non-secure contexts) block clipboard writes.
      // Fallback: open a small window so the user can copy manually.
      const w = window.open('', '_blank', 'width=720,height=520');
      if (w) {
        w.document.title = 'ChatMap debug snapshot';
        w.document.body.style.cssText = 'margin:0;padding:8px;font-family:monospace;font-size:11px;white-space:pre-wrap;background:#0b1220;color:#e5e7eb';
        w.document.body.textContent = json;
      } else {
        showToast('Clipboard blocked — check browser permissions.', 'error');
      }
    }
  }, [
    nodes,
    edges,
    runtime.inFlightTurnId,
    runtime.viewportTurnId,
    runtime.errorTurnId,
    runtime.collapsedFanIds,
    runtime.livePhaseEvents,
    runtime.wsLastEventMs,
    state.messages,
    showToast,
  ]);

  // Canvas-level click router. Child node components (tool_call, peer,
  // llm_round, turn_marker) already call `e.stopPropagation()` inside
  // their own onClick, so this only fires for nodes whose component
  // doesn't intercept — specifically the phase_stack_group backdrop.
  // That gives us a reliable "click anywhere in the stack's bounds"
  // nav without fighting CSS/React Flow pointer-events.
  const { focusBubble } = usePersonaChatUI();
  const handleNodeClick = React.useCallback(
    (_e: React.MouseEvent, node: Node) => {
      if (node.type !== 'phase_stack_group') return;
      const turnKey = (node.data as any)?.turnKey as string | undefined;
      if (turnKey) focusBubble(turnKey);
    },
    [focusBubble],
  );

  return (
    <div className={`h-full w-full relative ${darkMode ? 'bg-gray-950/40 chatdag-dark' : 'bg-gray-50'}`}>
      {isEmpty && (
        <div className="absolute inset-0 flex items-center justify-center text-xs opacity-60 pointer-events-none">
          No turns yet — send a message to see the DAG light up.
        </div>
      )}
      <ReactFlow
        nodes={tweenedNodes}
        edges={edges}
        nodeTypes={chatDagNodeTypes}
        fitView
        fitViewOptions={{ padding: FULL_FIT_PADDING, maxZoom: USER_FIT_MAX_ZOOM }}
        proOptions={{ hideAttribution: true }}
        minZoom={0.2}
        maxZoom={1.75}
        defaultEdgeOptions={{
          type: 'default', // bezier — fans read as fans (Alan's v0.2 ask)
          animated: true,  // marching-ants flow direction
          style: {
            strokeWidth: 2,
            stroke: darkMode ? '#10b981' : '#059669',
            strokeOpacity: 0.85,
          },
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        zoomOnScroll        // wheel = zoom (default, explicit for clarity)
        zoomOnPinch
        zoomOnDoubleClick={false} // double-click is reserved for focusToolCall
        onNodeClick={handleNodeClick}
      >
        <Background
          color={darkMode ? '#1f2937' : '#d1d5db'}
          gap={18}
          size={1}
        />
        <Controls
          position="bottom-right"
          showInteractive={false}
          className={darkMode ? '!bg-gray-900 !border-gray-700' : ''}
        />
        <MiniMap
          className="chatdag-minimap"
          pannable
          zoomable
          position="top-right"
          maskColor="rgba(2, 6, 23, 0.55)"
          maskStrokeColor="rgba(52, 211, 153, 0.85)"
          maskStrokeWidth={2}
          zoomStep={5}
          onNodeClick={(_e, node) => {
            // Phase-stack AABBs (and their collapsed-card form) navigate
            // exactly like the ControlsPanel rows: fitView the stack +
            // scroll the chat to the user bubble. Other minimap node
            // types (turn_marker / llm_round / tool_call / peer / fan
            // chips) are ignored here — they're individual elements,
            // not navigation targets.
            if (
              node.type !== 'phase_stack_group' &&
              node.type !== 'collapsed_phase_stack'
            ) {
              return;
            }
            const turnKey = (node.data as any)?.turnKey as string | undefined;
            if (!turnKey) return;
            const groupId =
              runtime.collapsedPhaseStackIds.has(turnKey) ||
              runtime.allPhaseStacksCollapsed
                ? `stack-${turnKey}`
                : `group-stack-${turnKey}`;
            try {
              fitView({
                nodes: [{ id: groupId }],
                padding: USER_FIT_PADDING,
                duration: USER_FIT_DURATION,
                maxZoom: USER_FIT_MAX_ZOOM,
              });
            } catch {
              /* node not rendered — no-op */
            }
            focusBubble(turnKey);
          }}
          // ReactFlow's MiniMap source extracts `style.background` and uses
          // it as the FILL of an inner <rect> drawn inside the SVG — but
          // ReactFlow's stylesheet ALSO sets `.react-flow__minimap {
          // background-color: #fff }` on the wrapper SVG. Without an
          // override that wrapper bg leaks through anywhere the inner
          // rect doesn't fully cover (rounded corners, alpha gaps), and
          // the alpha glass effect we set here renders white-tinted. The
          // chatdag-minimap class hooks an `index.css` override that
          // forces the wrapper to transparent, leaving the rect below as
          // the only bg — that's where this `style.background` lands.
          style={{
            background: darkMode
              ? 'rgba(11, 18, 32, 0.85)'
              : 'rgba(15, 23, 42, 0.82)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${darkMode ? 'rgba(52, 211, 153, 0.32)' : 'rgba(16, 185, 129, 0.38)'}`,
            borderRadius: 8,
            boxShadow:
              '0 4px 14px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            width: 140,
            height: 240,
          }}
          nodeStrokeWidth={1.5}
          nodeStrokeColor="rgba(15, 23, 42, 0.85)"
          nodeBorderRadius={2}
          nodeColor={(n: Node) => {
            switch (n.type) {
              case 'turn_marker':
                if ((n.data as any)?.role === 'user') {
                  return '#60a5fa';
                }
                return '#34d399';
              case 'llm_round':
                return (n.data as any)?.isSynthesis ? '#818cf8' : '#a78bfa';
              case 'peer':
                return '#94a3b8';
              case 'fan_collapsed_chip':
                return '#6b7280';
              case 'phase_stack_group': {
                // AABB fill mirrors PhaseStackGroupNode + ControlsPanel row
                // selection state. Realistic stack silhouette + color
                // identity = the panel/canvas/minimap all sing the same
                // tune for "this is where you are."
                const turnKey = (n.data as any)?.turnKey as string | undefined;
                if (turnKey && turnKey === runtime.errorTurnId) {
                  return 'rgba(244, 63, 94, 0.28)';        // rose
                }
                if (turnKey && turnKey === runtime.inFlightTurnId) {
                  return 'rgba(52, 211, 153, 0.30)';       // bright emerald
                }
                if (turnKey && turnKey === runtime.viewportTurnId) {
                  return 'rgba(52, 211, 153, 0.16)';       // dim emerald
                }
                return 'rgba(148, 163, 184, 0.10)';        // faint slate at rest
              }
              case 'collapsed_phase_stack':
                return '#9ca3af';
              case 'turn_separator':
                // Very subtle so the chapter break appears in the
                // minimap as a faint horizontal tick instead of a
                // bright bar that competes with content nodes.
                return 'rgba(148, 163, 184, 0.22)';
              case 'tool_call':
              default:
                return '#c084fc';
            }
          }}
        />
        {/* Controls panel pinned top-right directly UNDER the MiniMap.
            marginTop offsets past the MiniMap's 240px height + its
            ~10px top inset + a small gap. */}
        <Panel position="top-right" style={{ marginTop: 255 }}>
          <ControlsPanel />
        </Panel>
      </ReactFlow>

      {/* Debug snapshot button — top-right, above the minimap. Click
          copies a schema-versioned JSON blob (nodes + edges + runtime
          + trimmed recent messages + flag state) to the clipboard for
          iteration paste-backs. */}
      <button
        type="button"
        onClick={handleCopyDebugSnapshot}
        title={`Copy ChatMap debug snapshot (${nodes.length} nodes / ${edges.length} edges)`}
        className={`absolute top-2 right-2 z-20 px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wide border shadow-sm transition-colors ${
          darkMode
            ? 'bg-gray-900/90 text-emerald-300 border-emerald-500/40 hover:bg-gray-800/90 hover:border-emerald-400/70'
            : 'bg-white/90 text-emerald-700 border-emerald-500/50 hover:bg-emerald-50 hover:border-emerald-600'
        }`}
      >
        {`{ } JSON`}
      </button>
    </div>
  );
};

export interface ChatDagCanvasProps {
  primaryPersona?: any;
}

export const ChatDagCanvas: React.FC<ChatDagCanvasProps> = ({
  primaryPersona,
}) => (
  <ReactFlowProvider>
    <ChatDagInner primaryPersona={primaryPersona} />
  </ReactFlowProvider>
);

export default ChatDagCanvas;
